import { ref } from 'vue';
import type Pusher from 'pusher-js';
import { debugLog, debugWarn } from '@/utils/logger';

type EventHandler = (payload: any) => void;
type ConnectionState = 'disabled' | 'disconnected' | 'connecting' | 'connected' | 'unavailable';

const connectionState = ref<ConnectionState>('disabled');
const channels = new Map<string, Map<string, Set<EventHandler>>>();
const authorizations = new Set<AbortController>();
const profileSubscriptionHandlers = new Set<(profileId: number) => void>();
const subscribedProfileSockets = new Map<string, string>();
let client: Pusher | null = null;
let token = '';
let userId = 0;
let suspended = document.hidden;
let generation = 0;
let starting = false;
let connectDeadline: ReturnType<typeof setTimeout> | null = null;

function readConfig() {
  const key = import.meta.env.APP_REVERB_APP_KEY;
  if (!key) return null;
  const secure = ['https', 'wss'].includes(import.meta.env.APP_REVERB_SCHEME || window.location.protocol.replace(':', ''));
  const port = Number(import.meta.env.APP_REVERB_PORT || (secure ? 443 : 6001));
  if (!Number.isInteger(port) || port < 1 || port > 65535) return null;
  return {
    key, secure, port,
    host: import.meta.env.APP_REVERB_HOST || window.location.hostname,
    authEndpoint: `${import.meta.env.APP_BACKEND_API_ENDPOINT.replace(/\/$/, '')}/broadcasting/auth`,
  };
}

function clearDeadline() {
  if (connectDeadline) clearTimeout(connectDeadline);
  connectDeadline = null;
}

function disconnect() {
  generation++;
  starting = false;
  clearDeadline();
  authorizations.forEach((request) => request.abort());
  authorizations.clear();
  subscribedProfileSockets.clear();
  if (client) {
    client.unbind_all();
    client.connection.unbind_all();
    client.disconnect();
    client = null;
  }
  connectionState.value = readConfig() ? 'disconnected' : 'disabled';
}

function subscribe(channelName: string) {
  if (!client) return;
  const subscribingClient = client;
  const channel = client.subscribe(channelName);
  channel.bind('pusher:subscription_succeeded', () => {
    if (client !== subscribingClient || suspended || !channels.has(channelName)) return;
    debugLog('[realtime] private subscription ready');
    const profileMatch = /^private-profile\.(\d+)$/.exec(channelName);
    if (!profileMatch) return;
    const socket = subscribingClient.connection.socket_id;
    if (!socket || subscribedProfileSockets.get(channelName) === socket) return;
    subscribedProfileSockets.set(channelName, socket);
    for (const handler of profileSubscriptionHandlers) handler(Number(profileMatch[1]));
  });
  for (const [event, handlers] of channels.get(channelName) || []) {
    for (const handler of handlers) channel.bind(event, handler);
  }
}

async function connect() {
  const cfg = readConfig();
  if (!cfg || !token || !userId || suspended || !navigator.onLine || !channels.size || client || starting) return;
  const currentGeneration = generation;
  starting = true;
  connectionState.value = 'connecting';
  try {
    // Load the protocol client only for an authenticated, visible app.
    const { default: PusherClient } = await import('pusher-js');
    if (generation !== currentGeneration || suspended || !token || !channels.size) return;
    client = new PusherClient(cfg.key, {
      cluster: 'mt1', wsHost: cfg.host, wsPort: cfg.port, wssPort: cfg.port,
      forceTLS: cfg.secure, enabledTransports: ['ws', 'wss'], enableStats: false,
      activityTimeout: 60000, pongTimeout: 15000,
      channelAuthorization: {
        endpoint: cfg.authEndpoint,
        transport: 'ajax',
        customHandler: async (params, callback) => {
          const controller = new AbortController();
          const requestToken = token;
          authorizations.add(controller);
          const timeout = setTimeout(() => controller.abort(), 8000);
          try {
            const response = await fetch(cfg.authEndpoint, {
              method: 'POST',
              headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${requestToken}` },
              credentials: 'include', cache: 'no-store', signal: controller.signal,
              body: JSON.stringify({ socket_id: params.socketId, channel_name: params.channelName }),
            });
            if (!response.ok || requestToken !== token || generation !== currentGeneration) throw new Error('Realtime authorization unavailable');
            callback(null, await response.json());
          } catch {
            callback(new Error('Realtime authorization unavailable'), null);
          } finally {
            clearTimeout(timeout);
            authorizations.delete(controller);
          }
        },
      },
    });
    const armDeadline = () => {
      if (connectDeadline) return;
      // Stop retry traffic if Reverb is down. Tab return/retry starts again.
      connectDeadline = setTimeout(() => {
        disconnect();
        connectionState.value = 'unavailable';
      }, 30000);
    };
    client.connection.bind('state_change', ({ current }: { current: string }) => {
      if (current === 'connected') {
        clearDeadline();
        connectionState.value = 'connected';
        debugLog('[realtime] connected');
      } else {
        connectionState.value = current === 'connecting' ? 'connecting' : 'unavailable';
        armDeadline();
      }
    });
    armDeadline();
    for (const channelName of channels.keys()) subscribe(channelName);
  } catch {
    disconnect();
    connectionState.value = 'unavailable';
    debugWarn('Live updates unavailable; HTTP saving remains active.');
  } finally {
    if (currentGeneration === generation) starting = false;
  }
}

export function configureRealtimeSession(nextToken: string, nextUserId: number) {
  if (token === nextToken && userId === nextUserId) return;
  disconnect();
  if (userId && nextUserId !== userId) channels.clear();
  token = nextToken;
  userId = nextUserId;
  void connect();
}

export function pauseRealtime() {
  suspended = true;
  disconnect();
}

export function resumeRealtime() {
  suspended = false;
  void connect();
}

/** One catch-up signal per private profile subscription/socket, never per AI session. */
export function onProfileSubscribed(handler: (profileId: number) => void): () => void {
  profileSubscriptionHandlers.add(handler);
  return () => { profileSubscriptionHandlers.delete(handler); };
}

/** Subscriptions are shared by the app and reauthorized after reconnecting. */
export function listen(channel: string, event: string, handler: EventHandler, isPrivate = true): () => void {
  const channelName = isPrivate ? `private-${channel}` : channel;
  const newChannel = !channels.has(channelName);
  if (newChannel) channels.set(channelName, new Map());
  const events = channels.get(channelName)!;
  if (!events.has(event)) events.set(event, new Set());
  events.get(event)!.add(handler);
  if (client) {
    if (newChannel) subscribe(channelName);
    else client.channel(channelName)?.bind(event, handler);
  }
  void connect();
  return () => {
    const events = channels.get(channelName);
    client?.channel(channelName)?.unbind(event, handler);
    events?.get(event)?.delete(handler);
    if (!events?.get(event)?.size) events?.delete(event);
    if (!events?.size) {
      channels.delete(channelName);
      subscribedProfileSockets.delete(channelName);
      client?.unsubscribe(channelName);
    }
    if (!channels.size) disconnect();
  };
}

export function useRealtimeState() {
  return { connectionState };
}
